// src/pages/LessonPage.tsx
import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetCoursesQuery } from "../services/courses";
import StudentVideoPlayer from "../components/student/StudentVideoPlayer";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export default function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { data: courses = [], isLoading: coursesLoading } =
    useGetCoursesQuery(undefined);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Find the current lesson
  let currentLesson: any = null;
  let currentModule: any = null;
  let currentCourse: any = null;
  let lessonIndex = -1;
  let allLessons: any[] = [];

  for (const course of courses) {
    for (const module of course.modules || []) {
      for (const lesson of module.lessons || []) {
        allLessons.push({ course, module, lesson });
        if (lesson._id === lessonId) {
          currentLesson = lesson;
          currentModule = module;
          currentCourse = course;
          lessonIndex = allLessons.length - 1;
        }
      }
    }
  }

  const previousLesson = lessonIndex > 0 ? allLessons[lessonIndex - 1] : null;
  const nextLesson =
    lessonIndex < allLessons.length - 1 ? allLessons[lessonIndex + 1] : null;

  // Check if description needs expand/collapse button
  useEffect(() => {
    if (descriptionRef.current && currentLesson?.description) {
      const element = descriptionRef.current;
      // Check if content height exceeds the max-height (150px = ~4-5 lines)
      setShowExpandButton(element.scrollHeight > 150);
    }
  }, [currentLesson?.description]);

  // Mark lesson as complete
  const markComplete = async () => {
    setMarkingComplete(true);
    // TODO: Call API to mark lesson as complete
    // await markLessonComplete(lessonId);
    setTimeout(() => {
      setMarkingComplete(false);
      // Auto-advance to next lesson
      if (nextLesson) {
        navigate(`/dashboard/lesson/${nextLesson.lesson._id}`);
      }
    }, 500);
  };

  // Handle video completion
  const handleVideoEnd = () => {
    console.log("Video completed!");
    // Optionally auto-mark as complete when video ends
    if (!currentLesson.completed) {
      markComplete();
    }
  };

  // Handle video errors
  const handleVideoError = (error: string) => {
    console.error("Video error:", error);
    // You could show a toast notification here
  };

  const normalizeWorksheets = (
    lesson: any,
  ): { title: string; path: string }[] => {
    // Case 1: Proper worksheets array
    if (
      Array.isArray(lesson.worksheets) &&
      lesson.worksheets.length > 0 &&
      typeof lesson.worksheets[0].path !== "string"
    ) {
      return lesson.worksheets;
    }

    // Case 2: Stringified JSON inside worksheets[0].path
    if (
      Array.isArray(lesson.worksheets) &&
      lesson.worksheets.length > 0 &&
      typeof lesson.worksheets[0].path === "string"
    ) {
      try {
        const parsed = JSON.parse(lesson.worksheets[0].path);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse worksheets JSON", e);
      }
    }

    // Case 3: Legacy single worksheetPath
    if (lesson.worksheetPath) {
      return [{ title: "Worksheet", path: lesson.worksheetPath }];
    }

    return [];
  };

  // Loading state
  if (coursesLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  // Lesson not found
  if (!currentLesson) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Lesson Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The lesson you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <button
          onClick={() => navigate("/dashboard")}
          className="hover:text-teal-600 transition-colors"
        >
          Dashboard
        </button>
        <ChevronRight size={16} />
        <span>{currentCourse.title}</span>
        <ChevronRight size={16} />
        <span>{currentModule.title}</span>
        <ChevronRight size={16} />
        <span className="text-gray-900 font-medium">{currentLesson.title}</span>
      </div>

      {/* Lesson Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Title: {currentLesson.title}
        </h1>
      </div>

      {/* Secure Video Player */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <StudentVideoPlayer
          courseId={currentCourse._id}
          moduleId={currentModule._id}
          lessonId={currentLesson._id}
          // lessonTitle={currentLesson.title}
          onVideoEnd={handleVideoEnd}
          onError={handleVideoError}
          className="w-full"
        />
      </div>

      {/* Lesson Actions */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-6 flex-wrap gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          {!currentLesson.completed && (
            <button
              onClick={markComplete}
              disabled={markingComplete}
              className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <CheckCircle size={20} />
              {markingComplete ? "Marking..." : "Mark as Complete"}
            </button>
          )}
          {currentLesson.hasWorksheet && (
            <div className="mt-3 space-y-2">
              {normalizeWorksheets(currentLesson).map((worksheet, index) => (
                <a
                  key={index}
                  href={worksheet.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  📄
                  <span className="font-medium">
                    {worksheet.title || `Worksheet ${index + 1}`}
                  </span>
                </a>
              ))}

              {normalizeWorksheets(currentLesson).length === 0 && (
                <p className="text-sm text-gray-500 italic">
                  No worksheets attached
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {previousLesson && (
            <button
              onClick={() =>
                navigate(`/dashboard/lesson/${previousLesson.lesson._id}`)
              }
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <ChevronLeft size={20} />
              Previous
            </button>
          )}
          {nextLesson && (
            <button
              onClick={() =>
                navigate(`/dashboard/lesson/${nextLesson.lesson._id}`)
              }
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>

      <div>
        {currentLesson.completed ? (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle size={16} />
            <span>Completed</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Circle size={16} />
            <span>Not completed</span>
          </div>
        )}

        {currentLesson.description && (
          <div className="mt-4">
            <div
              ref={descriptionRef}
              className={`text-slate-700 overflow-hidden transition-all duration-300 ${
                isDescriptionExpanded ? "max-h-none" : "max-h-[150px]"
              }`}
              dangerouslySetInnerHTML={{ __html: currentLesson.description }}
            />
            {showExpandButton && (
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="mt-2 text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center gap-1 transition-colors"
              >
                {isDescriptionExpanded ? (
                  <>
                    See less <ChevronUp size={16} />
                  </>
                ) : (
                  <>
                    See more <ChevronDown size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-4 mt-4"></div>
      </div>

      {/* Lesson Content / Transcript (Optional) */}
      {currentLesson.content && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={24} />
            Lesson Notes
          </h2>
          <div
            className="prose prose-gray max-w-none"
            dangerouslySetInnerHTML={{ __html: currentLesson.content }}
          />
        </div>
      )}

      {/* Related Lessons */}
      {currentModule.lessons && currentModule.lessons.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Other Lessons in {currentModule.title}
          </h2>
          <div className="space-y-2">
            {currentModule.lessons.map((lesson: any, idx: number) => {
              const isCurrentLesson = lesson._id === lessonId;
              const lessonDuration = lesson.duration
                ? `${Math.floor(lesson.duration / 60)} min`
                : null;

              return (
                <button
                  key={lesson._id}
                  onClick={() => navigate(`/dashboard/lesson/${lesson._id}`)}
                  disabled={isCurrentLesson}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors text-left ${
                    isCurrentLesson
                      ? "bg-teal-50 border-2 border-teal-500 cursor-default"
                      : "hover:bg-gray-50 border border-gray-200 cursor-pointer"
                  }`}
                >
                  <div className="flex-shrink-0">
                    {lesson.completed ? (
                      <CheckCircle size={20} className="text-green-500" />
                    ) : (
                      <Circle size={20} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">
                      {lesson.title}
                    </div>
                    {lessonDuration && (
                      <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Clock size={14} />
                        {lessonDuration}
                      </div>
                    )}
                  </div>
                  {isCurrentLesson && (
                    <div className="text-sm text-teal-600 font-medium">
                      Currently Watching
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
